import moment from "moment";

import { generateExcel } from "src/utils/helper/functions";
import { BulkRecord, dataColumns, reasonsOf } from "./types";

/** Same rendering the table uses, so the file matches what was on screen. */
export const cellText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/**
 * `portfolioCode` → `Portfolio code`.
 *
 * Sentence case, not title case: splitting camelCase and capitalising only the
 * first word avoids headers like "Portfolio Code" in the exported sheet. The
 * table uppercases its headers in CSS, so this only shows in the workbook.
 */
export const columnLabel = (key: string): string => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const REASONS_HEADER = "Exclusion reasons";

/**
 * Flattens records into sheet rows.
 *
 * Columns come from the records themselves — the same derivation the table
 * uses — so a field the backend adds is exported without a frontend change.
 * Every row carries every column, because `json_to_sheet` takes its headers
 * from the first object and would otherwise drop keys that row happens to
 * lack.
 *
 * Reasons are joined onto one line so a record stays one row: the point of
 * the export is to work through the exclusions, and a row per reason would
 * make the count stop matching the summary.
 */
export const recordsToSheet = (
  records: BulkRecord[],
  includeReasons: boolean
): Record<string, string>[] => {
  const columns = dataColumns(records);

  return records.map((record) => {
    const row: Record<string, string> = {};
    columns.forEach((column) => {
      row[columnLabel(column)] = cellText(record[column]);
    });
    if (includeReasons) {
      row[REASONS_HEADER] = reasonsOf(record).join("; ");
    }
    return row;
  });
};

/** Downloads the records as a single-sheet workbook. */
export const exportRecords = (
  records: BulkRecord[],
  includeReasons: boolean,
  sheetName: string,
  fileName: string
): void => {
  generateExcel(
    [{ sheetName, sheetContent: recordsToSheet(records, includeReasons) }],
    `${fileName}-${moment().format("YYYY-MM-DD-HHmm")}`
  );
};
